// LICENSE_CODE ZON
'use strict'; /*jslint browser:true, es6:true, node:true, react:true*/
import React, {Fragment, useCallback, useContext, useEffect,
    useMemo, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import cn from 'classnames';
import {url2md5} from 'pub_config';
import {use_qs_with_set} from '../../../../hola/pub/lib/use_qs';
import {Settings_context} from '../../../../hola/pub/settings_ctx';
import Icon_x_close from '../../../../hola/pub/img/hola2023/icons/16/x_close.svg';
import Icon_chevron_down from '../../../../hola/pub/img/hola2023/icons/16/chevron_down.svg';
import Icon_plus from '../../../../hola/pub/img/hola2023/icons/16/plus.svg';
import Icon_copy from '../../../../hola/pub/img/hola2023/icons/16/copy.svg';
import Icon_check from '../../img/icons/16/check.svg';
import Icon_dots_half_circle from '../../img/icons/16/dots_half_circle.svg';
import Icon_chevron_right from '../../img/icons/16/chevron_right.svg';
import {Button} from '../../../../hola/pub/lib/button';
import {configured_proxies_get, DEFAULT_IPS_AMOUNT,
    download_format,
    get_generated_proxies, group_order_by_id,
    order_by_id, t_download_format} from '../dashboard/utils';
import {Proxy_type, t_generated_proxy, t_order,
    t_orders, t_zone_group} from '../../../../hola/pub/ui/pro/cp/orders';
import {Flag} from '../../../../hola/pub/lib/flag';
import {code2label} from 'zcountry';
import {use_pathname} from '../../../../hola/pub/lib/use_pathname';
import {Configuration_snippet, get_code} from './configure';
import {use_screen} from '../../../../hola/pub/lib/screen_util';
import Connect from '../../img/connect.svg';
import Arrow_right_up from '../../../../oculus/pub/img/icons/16/arrow_right_up.svg';
import {use_click_outside} from '../../../../hola/pub/lib/use_click_outside';
import {Usage_charges_modal} from '../dashboard/additional_usage_charges';
import {Rotate_countries, Rotate_error,
    Rotate_proxy, use_rotate_proxy} from './rotate_proxies';
import {use_debug_ui} from '../../../../hola/pub/lib/debug';
import {use_disappear} from '../dashboard/utils';
import etask from 'zetask';

import styles from './configure.module.less';

const cert_link = 'https://docs.toolip.io/proxy-networks/rotating-residential/ssl-certificate';

interface i_ui_config {
    title?: string,
    label_generic?: string
    label_insomniac?: string
    download_icon?: React.ReactNode
    download_list_icon?: React.ReactNode;
    hide_emoji?: boolean
    docs_caption?: string
    docs_link?: string
    download_select_cls?: string;
    website_name?: 'toolip' | 'oculus';
    show_rotate?: boolean;
    show_rotate_error_icon?: boolean;
    btn_loader?: React.ReactNode
    flag_round?: boolean;
    render_ip_whitelist?: (_id: string, type: string)=>JSX.Element;
    render_download_button?: (caption: string, on_click: any)=>JSX.Element;
    render_new_conf_button?: (on_click: ()=>void)=>JSX.Element;
}

interface i_configured_proxy_list_props {
    orders: t_orders;
    zone_groups?: t_zone_group[];
    active?: boolean;
    get_product_name: (type: Proxy_type)=>string;
    ui_config?: i_ui_config;
    free_fake_zone?: t_order;
    need_login?: ()=>void;
}

interface i_free_plan_settings {
    generated: t_generated_proxy;
    title?: string;
    hide_emoji: boolean;
}

const Free_plan_settings = ({generated, title, hide_emoji}:
i_free_plan_settings)=>{
    const {theme} = useContext(Settings_context);
    const [copied, set_copied] = useState<number | null>(null);
    const {show: show_copied} = use_disappear(1);
    const on_copy = (value, i)=>void etask(function*(){
        try {
            yield navigator.clipboard.writeText(value);
        } catch(e){}
        set_copied(i);
        show_copied();
        yield etask.sleep(1000);
        set_copied(null);
    });
    const details = [
        {label: 'HOST', value: generated?.host},
        {label: 'PORT', value: generated?.port},
        {label: 'PROXY USERNAME', value: generated?.login},
        {label: 'PASSWORD', value: generated?.password}
    ];
    const show_copy_btn = !generated?.login.endsWith('***');
    return <div className={styles.grid_container}>
      <p className={cn(theme.p16, theme.med, styles.free_plan_title)}>
        {title ? title : <>
          You have configured 1/1&nbsp;
          <span className={styles.flag_container}>
            <Flag country={generated?.country} size={16} round />
            <span className={cn(styles.country, theme.P, theme.p16,
              theme.med)}>
              {code2label(generated?.country ?? '')}
            </span>
          </span>
          &nbsp;proxies in this plan {hide_emoji ? '' : '🤖✨'}
        </>}
      </p>
      <div className={styles.grid}>
        {details.map(({label, value}, i)=>
          <div key={i} className={styles.grid_item}>
            <p className={cn(theme.p12, theme.med, theme.color_n300)}>
              {label}</p>
            <div className={styles.value_container}
              onClick={()=>on_copy(value, i)}>
              <p className={cn(theme.p14, theme.med)}>{value}</p>
              {show_copy_btn && <button className={styles.copy_btn}
                onClick={()=>on_copy(value, i)}>
                  <Icon_copy className={cn(styles.copy_icon,
                    copied == i ? styles.hidden : '')}/>
                  <Icon_check className={cn(styles.check_icon,
                    copied != i ? styles.hidden : '')}/>
                  <span className={cn(styles.copied,
                    copied == i && styles.visible)}>
                    Copied!</span>
              </button>}
            </div>
          </div>
        )}
      </div>
    </div>;
};

export const Configured_proxy_list = (props: i_configured_proxy_list_props)=>{
    const {orders, zone_groups, active, ui_config, free_fake_zone,
        need_login} = props;
    const {qs, set_qs} = use_qs_with_set();
    const {params, set} = use_pathname();
    const {theme} = useContext(Settings_context);
    const _id = params.configure_proxy;
    const item = free_fake_zone ? free_fake_zone :
        order_by_id(orders, _id) ?? group_order_by_id(zone_groups, _id);
    const group = zone_groups?.find(g=>g._id==item?.group_id);
    const is_free = item?.free_zone;
    const {width} = use_screen();
    const debug_configure_panel = use_debug_ui('updated_configure_panel');
    const certificate_url = ()=>{
        const website = ui_config?.website_name || 'toolip';
        return `/www/${website}/pub/ssl_${website}_certificate.cer`;
    };
    const [show_uc_modal, set_show_uc_modal] = useState(false);
    useEffect(()=>{
        if (active && (params.zone_group ? zone_groups : orders) && !item)
        {
            set({configure_proxy: undefined, type: undefined,
                zone_group: undefined});
        }
    }, [active, orders, item]);
    const visible = active && !!item;
    useEffect(()=>{
        if (visible)
            document.body.classList.add('hola_modal-no_body_scroll_stack');
        else
            document.body.classList.remove('hola_modal-no_body_scroll_stack');
    }, [visible]);
    const {rotate_state, do_rotate} = use_rotate_proxy(item);
    const generated = get_generated_proxies(item)
        .filter(p=>p.manually_changed);
    const ips_amount = item ? item.unit == 'gb'
        ? DEFAULT_IPS_AMOUNT : item.ips_amount : 1;
    const show_rotate_proxy = ui_config.show_rotate
        && [Proxy_type.EV_ISP, Proxy_type.ISPPREM].includes(item?.type);
    const docs_links_comp = <div className={styles.https_docs}>
      <p className={cn(width < 768 ? theme.p14 : theme.p12)}>
        {ui_config?.docs_caption ??
        'Need instructions on how to download and install?'}</p>
      <Link className={cn(theme.color_n700, styles.docs_link)}
        to={{pathname: ui_config?.docs_link ?? cert_link}}
        target="_blank">
        Visit Documentation
      </Link>
    </div>;
    const article_section = debug_configure_panel ?
      <div className={cn(styles.article_section,
        debug_configure_panel && styles.article_section_debug)}>
        <div className={cn(styles.logo,
          debug_configure_panel && styles.logo_debug)}><Connect/></div>
        <div className={debug_configure_panel ?
          styles.content_debug : styles.content}>
          <p className={cn(theme.p14, theme.med)}>
            Getting Started with Your Toolip Proxy</p>
          <p className={cn(theme.p14, theme.reg, styles.middle_p,
            theme.color_n500)}>
            Need assistance with proxy integration?<br/>We're here to help!</p>
          <div className={styles.links_container}>
            <a href='https://docs.toolip.io/integration-guides/openbullet'
              target="_blank" rel='noopener noreferrer'
              className={cn(theme.P, theme.p14, theme.med)}>Automation</a>
            <a href='https://docs.toolip.io/integration-guides/android'
              target="_blank" rel='noopener noreferrer'
              className={cn(theme.P, theme.p14, theme.med)}>Systems</a>
            <a href='https://docs.toolip.io/integration-guides/adspower'
              target="_blank" rel='noopener noreferrer'
              className={cn(theme.P, theme.p14, theme.med)}>Browsers</a>
            <a href='https://docs.toolip.io/integration-guides/apify'
              target="_blank" rel='noopener noreferrer'
              className={cn(theme.P, theme.p14, theme.med)}>Testing</a>
            <a href='https://docs.toolip.io/integration-guides/browserscan'
              target="_blank" rel='noopener noreferrer'
              className={cn(theme.P, theme.p14, theme.med)}>Scraping tools</a>
          </div>
        </div>
      </div> :
      <div className={styles.article_section}>
        <div className={styles.logo}><Connect/></div>
        <div className={styles.content}>
          <p className={cn(theme.p14, theme.med)}>
            Getting Started with Your Toolip Proxy</p>
          <p className={cn(theme.p12, theme.reg, styles.middle_p)}>
            A simple guide for configuring your Toolip proxy across different
            systems</p>
          <Button caption={<>Go to article <Arrow_right_up/></>}
            size={'sm'} round={false} cls={styles.button} on_click={()=>
            window.open('https://support.toolip.io/en/articles/10079457-getting'+
              '-started-with-your-toolip-proxy', '_blank')}/>
        </div>
      </div>;
    const is_residential_zone = [Proxy_type.RES, Proxy_type.EV_RES,
        Proxy_type.SNK].includes(item?.type);
    const virtual_generated = configured_proxies_get(item?._id) ??
      item?.generated_proxies;
    const show_custom_generic_config = virtual_generated;
    const show_zone_menu = use_debug_ui('group_zone_menu');
    const show_add_zone = params.zone_group && show_zone_menu;
    const is_auth_ip = item?.auth_type=='ip';
    const config_new_zone = ()=>{
        set({configure_proxy: undefined, type: undefined,
          zone_group: undefined, configure_group: item.group_id});
    };
    const on_add_new_zone = ()=>{
        if (group.enable_additional_usage_charges || !show_zone_menu)
            config_new_zone();
        else
            set_show_uc_modal(true);
    };
    const free_fake_signup = <div className={styles.free_signup_configure}
      onClick={need_login}>
      <Icon_dots_half_circle/>
      <div className={styles.free_signup_configure_wrapper}>
        <p className={cn(width < 1700 ? theme.p12 : theme.p16, theme.med,
          theme.color_n700)}>Sign up</p>
        <p className={cn(width < 1700 ? theme.p12 : theme.p16, theme.med,
          theme.color_n700)}>&nbsp;to get full access</p>
        <Icon_chevron_right/>
      </div>
    </div>;
    return <div className={cn(styles.overlay,
      visible && styles.visible)} onClick={()=>{
        set({configure_proxy: undefined, type: undefined,
          zone_group: undefined});
        set_qs({success: undefined, example: undefined});
      }}>
      <div className={cn(styles.configure_proxy, styles[item?.type])}
        onClick={ev=>{ ev.stopPropagation(); }}>
        <div className={styles.header}>
          <p className={cn(theme.p14, theme.med)}>
            {ui_config?.title ?? 'Configured proxies'}</p>
          <Icon_x_close onClick={()=>{
            set({configure_proxy: undefined, type: undefined,
              zone_group: undefined});
            set_qs({success: undefined, example: undefined});
          }} className={styles.close}/>
        </div>
        {item?.unit=='gb' ? <Fragment>
          <div className={styles.config_top}>
            <div className={styles.success}>
              {is_free ?
                <Free_plan_settings generated={generated[0]}
                  hide_emoji={ui_config?.hide_emoji}
                  title={"Here's your Free Shared Datacenter Proxy!"}/> :
                <>
                  <p className={cn(styles.sub_title, theme.med)}>
                    {ui_config?.hide_emoji ? '': '🎉'}
                    Your proxies have been configured successfully!
                    {ui_config?.hide_emoji ? '': '🐣'}</p>
                  <p className={styles.step}>Here are your&nbsp;
                    {props.get_product_name(item?.type)}</p>
                </>}
            </div>
          </div>
          <Configuration_snippet order={item}
            generated={generated?.map(g=>({...g, ...virtual_generated?.[0] ?
              {port: ''+virtual_generated[0].port} : {}}))}
            custom_snippets={show_custom_generic_config ? [
              {caption: ui_config?.label_generic ?? 'Generic',
                lang_key: 'ip_generic',
                get_code: ()=>get_code(virtual_generated, ':', is_auth_ip)},
              {caption: ui_config?.label_insomniac ?? 'Insomniac',
                lang_key: 'ip_insomniac',
                get_code: ()=>
                  get_code(virtual_generated, ',', is_auth_ip, true)},
            ] : []}
            lang={show_custom_generic_config ? 'ip_generic': 'curl'}
            with_line_numbers/>
          <div className={styles.proxy_buttons}>
            {show_rotate_proxy && <Rotate_proxy
              state={rotate_state} do_rotate={do_rotate}
              order={item} btn_loader={ui_config.btn_loader}
              icon={ui_config.download_list_icon}/>}
            {virtual_generated?.length &&
            <Download_format ui_config={ui_config}
              order={item} count={virtual_generated?.length}
              generated_proxies={virtual_generated}/>}
          </div>
          {rotate_state.data?.error &&
            <Rotate_error show_icon={ui_config.show_rotate_error_icon}/>}
          {is_residential_zone && !free_fake_zone && <>
            <div className={styles.config_https}>
              <div className={styles.https_download}>
                <p className={cn(theme.p14, theme.med, theme.color_n700)}>
                  Working with HTTPS?</p>
                <Button size={width < 768 ? 'md' : 'xs'}
                  caption='Download HTTPS certificate'
                  disabled={false} color='transparent' no_jump round={false}
                  to={url2md5(certificate_url())}/>
              </div>
            </div>
            {show_add_zone && <div className={styles.add_new_zone}>
              <Usage_charges_modal is_open={show_uc_modal}
                on_close={()=>{ set_show_uc_modal(false); }}
                group={group} on_confirm={config_new_zone}
              />
              <Button size="sm" color='transparent' no_jump round={false}
                caption={<><Icon_plus/> Add a new zone</>}
                on_click={on_add_new_zone}
              />
            </div>}
          </>}
          {show_custom_generic_config && !free_fake_zone &&
            ui_config?.render_new_conf_button(()=>{
              set({configure_proxies: item._id, configure_proxy: undefined,
                type: item.type});
              set_qs({example: '1'});
            })}
          {ui_config?.render_ip_whitelist?.(_id, item?.type)}
          {free_fake_zone ? free_fake_signup : docs_links_comp}
        </Fragment> : <Fragment>
          <div className={styles.config_top}>
            {qs.success ? <div className={styles.success}>
              <p className={cn(theme.p14, theme.med)}>
                {ui_config?.hide_emoji ? '': '🎉'} Your proxies
                have been configured successfully!
                {ui_config?.hide_emoji ? '': '🐣'}</p>
            </div> : !is_free && <div className={styles.summary}>
              <p className={cn(styles.sub_title, theme.med)}>
                You have configured {generated.length}/{ips_amount} proxies
                in this plan {ui_config?.hide_emoji ? '': '🤖✨'}
              </p>
            </div>}
            <Rotate_countries order={item} btn_loader={ui_config.btn_loader}
              flag_round={ui_config.flag_round} />
          </div>
          {is_free ?
            <Free_plan_settings generated={generated[0]}
              hide_emoji={ui_config?.hide_emoji}/> :
            generated?.length > 0 &&
              <p className={cn(styles.step, styles.step_code)}>
                Here is an example code for
                your {props.get_product_name(item?.type)}
              </p>}
          <Configuration_snippet order={item} generated={generated}
            custom_snippets={[
              {caption: ui_config?.label_generic ?? 'Generic',
                lang_key: 'ip_generic',
                get_code: ()=>get_code(generated, ':', is_auth_ip)},
              {caption: ui_config?.label_insomniac ?? 'Insomniac',
                lang_key: 'ip_insomniac',
                get_code: ()=>get_code(generated, ',', is_auth_ip, true)},
            ]}
            lang="ip_generic" with_line_numbers/>
          <div className={styles.proxy_buttons}>
            {show_rotate_proxy && <Rotate_proxy
              state={rotate_state} do_rotate={do_rotate}
              order={item} btn_loader={ui_config.btn_loader}
              icon={ui_config.download_list_icon}/>}
            {generated?.length > 0 &&
              <Download_format ui_config={ui_config} order={item}
                omit_credentials={is_auth_ip}
                count={generated?.length}/>}
          </div>
          {rotate_state.data?.error &&
            <Rotate_error show_icon={ui_config.show_rotate_error_icon}/>}
          {ui_config?.render_ip_whitelist?.(_id, item?.type)}
          {ui_config?.docs_link && docs_links_comp}
          {free_fake_zone ? free_fake_signup : is_free && article_section}
        </Fragment>}
      </div>
    </div>;
};

interface i_download_format_props {
    ui_config: i_ui_config;
    order: t_order;
    count: number;
    generated_proxies?: t_generated_proxy[];
    omit_credentials?: boolean;
}
export const Download_format = (props: i_download_format_props)=>{
    const {ui_config, order, count,
        generated_proxies, omit_credentials} = props;
    const debug_configure_panel = use_debug_ui('updated_configure_panel');
    const [opened, set_opened] = useState(false);
    const toggle = (value?: boolean)=>{
        if (value===undefined)
            set_opened(v=>!v);
        else
            set_opened(value);
    };
    const default_render_button = useCallback((caption: string,
        on_click: any)=>
        {
        return <Button
          size={'md'} color='black'
          no_jump round={false}
          caption={<>{caption}{ui_config?.download_icon}</>}
          on_click={on_click}
        />;
    }, [ui_config?.download_icon]);
    const proxies = generated_proxies ?? order?.generated_proxies;
    const render_button = ui_config?.render_download_button
        || default_render_button;
    const download_items = useMemo(()=>{
        const on_download = (format: t_download_format)=>()=>{
            toggle(false);
            download_format(format, order?.type, proxies, omit_credentials);
        };
        return [
            {
                title: 'JSON',
                on_click: on_download('json'),
            },
            {
                title: 'CSV',
                on_click: on_download('csv'),
            },
            {
                title: 'Generic',
                on_click: on_download('generic'),
            },
            {
                title: 'Insomniac',
                on_click: on_download('insomniac'),
            },
        ];
    }, [order?.type, proxies, omit_credentials]);
    return <div className={cn(styles.download_format,
      debug_configure_panel && styles.download_format_debug)}>
      <Download_select
        ui_config={ui_config}
        items={download_items}
        count={count}
        opened={opened}
        toggle={toggle}
        render_btn={render_button}
        order_id={order?._id}
      />
    </div>;
};

interface i_download_select_props {
    ui_config: i_ui_config;
    items: {title: string, on_click: any}[];
    opened: boolean;
    toggle: (v?: boolean)=>void;
    count: number;
    render_btn: i_ui_config['render_download_button'];
    order_id?: string;
}
const Download_select = (props: i_download_select_props)=>{
    const {items, ui_config, count, opened, toggle, render_btn,
        order_id} = props;
    const select_ref = useRef<HTMLDivElement>();
    use_click_outside(select_ref, ()=>toggle(false));
    const is_free_fake = order_id == 'free_fake';
    const ips_count = count==1
        ? `${count} IP`
        : `${count} IP's`;
    const select_cls = cn(styles.download_select, opened && styles.opened,
        ui_config?.download_select_cls || styles.toolip_download_select,
        is_free_fake && styles.download_select_disabled);
    const [delay_btns_panel_pos, set_delay_btns_panel_pos] = useState(true);
    useEffect(()=>{
        if (!opened)
        {
            const timer = setTimeout(()=>set_delay_btns_panel_pos(true), 300);
            return ()=>clearTimeout(timer);
        }
        set_delay_btns_panel_pos(false);
    }, [opened]);
    if (is_free_fake)
        return;
    return <div className={select_cls} ref={select_ref}>
      <p data-elem="title" onClick={()=>is_free_fake ? null : toggle()}>
        Download format
        <span data-elem="toggle_icon">
          {ui_config?.download_list_icon || <Icon_chevron_down/>}
        </span>
      </p>
      <p data-elem="tooltip">{ips_count} Ready for Download</p>
      <ul data-elem="list">
        {items.map(item=>{
          return <li key={item.title} data-elem="item"
            onClick={item.on_click}>
            <span data-elem="item_title">{item.title}</span>
            <span data-elem="item_icon">{ui_config?.download_icon}</span>
          </li>;
        })}
      </ul>
      <div data-elem="buttons_panel" className={cn(opened && styles.visible,
        delay_btns_panel_pos && styles.closed)}>
        <div className={styles.container}>
          {items.map(item=>{
            return <Fragment key={item.title}>
              {render_btn(item.title, item.on_click)}
            </Fragment>;
          })}
        </div>
      </div>
    </div>;
};
